import { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

/**
 * Predefined color palette - professional design colors
 */
const COLOR_PALETTE = [
  // Grayscale
  [
    '#000000',
    '#1F2937',
    '#374151',
    '#6B7280',
    '#9CA3AF',
    '#D1D5DB',
    '#E5E7EB',
    '#F3F4F6',
    '#FFFFFF',
  ],
  // Red
  [
    '#450A0A',
    '#7F1D1D',
    '#991B1B',
    '#DC2626',
    '#EF4444',
    '#F87171',
    '#FCA5A5',
    '#FECACA',
    '#FEE2E2',
  ],
  // Orange
  [
    '#431407',
    '#7C2D12',
    '#9A3412',
    '#EA580C',
    '#F97316',
    '#FB923C',
    '#FDBA74',
    '#FED7AA',
    '#FFEDD5',
  ],
  // Yellow
  [
    '#422006',
    '#713F12',
    '#854D0E',
    '#CA8A04',
    '#EAB308',
    '#FACC15',
    '#FDE047',
    '#FEF08A',
    '#FEF9C3',
  ],
  // Green
  [
    '#052E16',
    '#14532D',
    '#166534',
    '#16A34A',
    '#22C55E',
    '#4ADE80',
    '#86EFAC',
    '#BBF7D0',
    '#DCFCE7',
  ],
  // Teal
  [
    '#042F2E',
    '#134E4A',
    '#115E59',
    '#0D9488',
    '#14B8A6',
    '#2DD4BF',
    '#5EEAD4',
    '#99F6E4',
    '#CCFBF1',
  ],
  // Blue
  [
    '#0C1929',
    '#1E3A5F',
    '#1E40AF',
    '#2563EB',
    '#3B82F6',
    '#60A5FA',
    '#93C5FD',
    '#BFDBFE',
    '#DBEAFE',
  ],
  // Indigo
  [
    '#1E1B4B',
    '#312E81',
    '#3730A3',
    '#4F46E5',
    '#6366F1',
    '#818CF8',
    '#A5B4FC',
    '#C7D2FE',
    '#E0E7FF',
  ],
  // Purple
  [
    '#2E1065',
    '#4C1D95',
    '#6B21A8',
    '#9333EA',
    '#A855F7',
    '#C084FC',
    '#D8B4FE',
    '#E9D5FF',
    '#F3E8FF',
  ],
  // Pink
  [
    '#500724',
    '#831843',
    '#9D174D',
    '#DB2777',
    '#EC4899',
    '#F472B6',
    '#F9A8D4',
    '#FBCFE8',
    '#FCE7F3',
  ],
];

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  onClose?: () => void;
  className?: string;
}

/**
 * ColorPicker - Color selection component with palette and custom input
 */
export function ColorPicker({
  value,
  onChange,
  onClose,
  className,
}: ColorPickerProps) {
  const [customColor, setCustomColor] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Handle color selection
  const handleColorClick = useCallback(
    (color: string) => {
      onChange(color);
    },
    [onChange]
  );

  // Handle custom color input
  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setCustomColor(color);
    if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
      onChange(color);
    }
  };

  // Handle hex input
  const handleHexInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let hex = e.target.value.toUpperCase();
    if (!hex.startsWith('#')) hex = '#' + hex;
    setCustomColor(hex);
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      onChange(hex);
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'bg-paper-2 rounded-card ring-1 ring-line border border-line p-3',
        'w-[280px]',
        className
      )}
    >
      {/* Color Grid */}
      <div className="space-y-1 mb-3">
        {COLOR_PALETTE.map((row, rowIndex) => (
          <div key={rowIndex} className="flex gap-1">
            {row.map((color) => (
              <button
                key={color}
                onClick={() => handleColorClick(color)}
                className={cn(
                  'w-6 h-6 rounded transition-transform hover:scale-110',
                  'border border-line',
                  'flex items-center justify-center',
                  value.toUpperCase() === color.toUpperCase() &&
                    'ring-2 ring-primary ring-offset-1'
                )}
                style={{ backgroundColor: color }}
              >
                {value.toUpperCase() === color.toUpperCase() && (
                  <Check
                    className={cn(
                      'w-3 h-3',
                      isLightColor(color) ? 'text-ink' : 'text-white'
                    )}
                  />
                )}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Custom Color Input */}
      <div className="flex items-center gap-2 pt-2 border-t border-line">
        <input
          type="color"
          value={customColor}
          onChange={handleCustomColorChange}
          className="w-8 h-8 rounded cursor-pointer border border-line"
        />
        <input
          type="text"
          value={customColor.toUpperCase()}
          onChange={handleHexInput}
          placeholder="#000000"
          className="flex-1 px-2 py-1.5 text-sm font-mono border border-line rounded focus:ring-2 focus:ring-primary focus:border-primary"
          maxLength={7}
        />
      </div>
    </div>
  );
}

/**
 * Check if a color is light (for contrast calculation)
 */
function isLightColor(hex: string): boolean {
  const color = hex.replace('#', '');
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}
